import type { Config } from "@codeshare/config";
import type { PeerDiagnostics } from "@codeshare/session";

export interface DiagnosticsInput {
  app: { version: string; sha: string; builtAt: number };
  status: string;
  config: Config | undefined;
  peers: PeerDiagnostics[];
  environment: { userAgent: string; crossOriginIsolated: boolean; online: boolean };
  now?: number;
}

/**
 * A JSON report for bug reports and support. Leaves out everything secret or identifying beyond
 * what's needed: no room link, secret or topic, no ICE credentials (only whether one is set), no
 * addresses or ports (the stats summary drops them), no client ids.
 */
export function diagnosticsReport(input: DiagnosticsInput): string {
  const { config } = input;
  const report = {
    generatedAt: new Date(input.now ?? Date.now()).toISOString(),
    app: input.app,
    environment: input.environment,
    room: { status: input.status },
    config: config && {
      iceTransportPolicy: config.iceTransportPolicy,
      iceServers: config.iceServers.map((s) => ({
        urls: s.urls,
        hasCredential: Boolean(s.username || s.credential),
      })),
      signaling: config.signaling.map((s) =>
        s.type === "nostr" ? { type: s.type, relays: s.relays } : { type: s.type },
      ),
      irohRelays: config.irohRelays,
    },
    peers: input.peers,
  };
  return JSON.stringify(report, null, 2);
}

/** "direct", "via STUN", "relayed (TURN)" or "relayed (iroh)", from the connection stats. */
export function describePath(
  pair: PeerDiagnostics["connection"]["pair"],
  connection?: Pick<PeerDiagnostics["connection"], "transport" | "relayUrl">,
): string {
  if (connection?.transport === "iroh") {
    const relay = connection.relayUrl?.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `relayed through iroh${relay ? ` (${relay})` : ""}`;
  }
  if (!pair) return "connecting…";
  const types = [pair.local.type, pair.remote.type];
  const protocol = pair.local.relayProtocol ?? pair.local.protocol;
  const suffix = protocol ? `, ${protocol}` : "";
  if (types.includes("relay")) return `relayed through TURN (${types.join(" ↔ ")}${suffix})`;
  if (types.includes("srflx") || types.includes("prflx"))
    return `direct, through NAT (${types.join(" ↔ ")}${suffix})`;
  return `direct (${types.join(" ↔ ")}${suffix})`;
}
